import React from "react";
import { formatDecimal, formatMoney, formatDate, formatDateTime, formatText } from "../../utils/formatters";
// (2026-07-22 Viajes) Fuente única de los 15 campos del nodo `/viajes`.
import { VIAJE_CAMPOS } from "../../utils/viajeCampos";
// (2026-08-02, ítem 32) Helpers de línea COMPARTIDOS con la modal de factura.
// Esta modal calculaba el total de línea con su propia fórmula (que ignoraba el
// descuento por monto fijo y el recargo) y leía las columnas planas sin resolver
// las `_aplicada`. Se importan en vez de duplicarse: una sola fórmula, un solo
// sitio donde arreglarla.
import {
  valorAplicado,
  precioUnitarioAplicado,
  totalLineaMostrado,
} from "../Invoices/InvoiceDetailView";

function ModalProformas({ isOpen, onClose, message }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center">

      {/* MODAL */}
      <div className="bg-white rounded-2xl shadow-sirius-lg w-full max-w-[1100px] max-h-[90vh] overflow-hidden relative flex flex-col">

        {/* HEADER */}
        <div className="px-6 md:px-8 py-5 border-b border-slate-200 bg-white">
          <h6 className="text-lg md:text-xl font-display font-bold text-ink-900 text-center">
            Detalles de la Proforma
          </h6>
        </div>

        {/* BODY CON SCROLL */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4">
          <div className="flex flex-wrap">
            <div className="w-full px-4">
              <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-lg bg-white border-0">

                {/* Datos principales de la pre-factura */}
                <hr className="my-6 border-b border-blueGray-300"/>
                <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2 px-5">
                  Información del Documento
                </h6>
                <div className="px-5 flex justify-between">
                  <div className="lg:w-4/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Fecha de Proformas:</label>
                    <span className="text-blueGray-700">{message.fecha_factura ? formatDate(message.fecha_factura) : ''}</span>
                  </div>
                  <div className="lg:w-4/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Correlativo:</label>
                    <span className="text-blueGray-700">{message.correlativo_interno}</span>
                  </div>
                  {/* (#117 Increment 1b) "Serial" RETIRADO: el borrador (proforma) no
                      expone su nº de control (ex-`serial`); se asigna al convertir. */}
                </div>

                {/* Cliente (comprador) */}
                <hr className="my-6 border-b border-blueGray-300"/>
                <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2 px-5">
                  Datos del Cliente
                </h6>
                <div className="px-5 flex justify-between">
                  <div className="lg:w-2/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">RIF:</label>
                    <span className="text-blueGray-700">{message.cliente?.rif ?? message.cliente_rif}</span>
                  </div>
                  <div className="lg:w-5/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Razón Social:</label>
                    <span className="text-blueGray-700">{message.cliente?.nombre ?? message.cliente_nombre}</span>
                  </div>
                  <div className="lg:w-5/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Dirección:</label>
                    <span className="text-blueGray-700">{message.zona?.toUpperCase()}</span>
                  </div>
                </div>

                {/* Documento y Estado */}
                <hr className="my-6 border-b border-blueGray-300"/>
                <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2 px-5">
                  Estado del Documento
                </h6>
                <div className="px-5 flex justify-between">
                  <div className="lg:w-4/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Tipo de documento:</label>
                    <span className="text-blueGray-700">
                      {message.tipo_documento === 'FC' ? 'FACTURA' : message.tipo_documento === 'NC' ? 'NOTA DE CREDITO' : 'NOTA DE DEBITO'}
                    </span>
                  </div>
                  <div className="lg:w-4/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Estado:</label>
                    <span className="text-blueGray-700">{message.estatus.toUpperCase()}</span>
                  </div>
                  {/* (2026-05-13) Req 2: moneda de la proforma */}
                  <div className="lg:w-4/12 text-start">
                    <label className="text-blueGray-700 font-bold me-3">Moneda:</label>
                    <span className="text-blueGray-700">
                      {message.moneda
                        ? `${message.moneda.codigo} (${message.moneda.simbolo})`
                        : '—'}
                      {/* (2026-08-02, ítem 32-iii) La tasa NO se mostraba en esta
                          modal (solo en la de factura). Se añade con 4 decimales,
                          igual que el PDF y la vista pública. */}
                      {message.tasa && (
                        <span className="text-xs text-blueGray-400 ml-2">
                          tasa: {formatDecimal(message.tasa, 4)}
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                {/* (2026-05-13) Req 1: Bloque de facturación de tercero — solo si doc_tercero=true */}
                {message.doc_tercero && (
                  <>
                    <hr className="my-6 border-b border-blueGray-300"/>
                    <div className="px-5">
                      <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2">
                        Datos del Tercero (Afiliada)
                      </h6>
                      <div className="flex flex-wrap justify-between">
                        <div className="lg:w-4/12 text-start">
                          <label className="text-blueGray-700 font-bold me-3">Tercero:</label>
                          <span className="text-blueGray-700">
                            {message.tercero
                              ? `${formatText(message.tercero.nombre_empresa)} (${message.tercero.rif})`
                              : '—'}
                          </span>
                        </div>
                        <div className="lg:w-4/12 text-start">
                          <label className="text-blueGray-700 font-bold me-3">Comisión:</label>
                          <span className="text-blueGray-700">
                            {message.comision_servicio != null
                              ? `${formatDecimal(message.comision_servicio)} %`
                              : '—'}
                          </span>
                        </div>
                        <div className="lg:w-4/12 text-start">
                          <label className="text-blueGray-700 font-bold me-3">Comisión aplicada:</label>
                          <span className="text-blueGray-700">
                            {message.comision_calculada_aplicada != null
                              ? formatMoney(message.comision_calculada_aplicada, message.moneda)
                              : '—'}
                          </span>
                        </div>
                      </div>
                      {message.product_tercero && (
                        <div className="mt-2">
                          <label className="text-blueGray-700 font-bold me-3">Producto de comisión:</label>
                          <span className="text-blueGray-700">
                            {formatText(message.product_tercero.sku)} — {formatText(message.product_tercero.nombre)}
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {/* (2026-07-22 Viajes) Nodo `/viajes` — datos de pasajero y
                    boleto. Render ESTRICTAMENTE condicional: una proforma sin
                    boletos se ve exactamente igual que antes. Un documento puede
                    cubrir N boletos (1:N). */}
                {(message.viajes || []).length > 0 && (
                  <>
                    <hr className="my-6 border-b border-blueGray-300"/>
                    <div className="px-5">
                      <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2">
                        Datos del Viaje (Boletería Aérea)
                        {message.es_boleteria_aerea && (
                          <span className="ml-2 inline-block bg-twilight-indigo-100 text-twilight-indigo-700 py-0.5 px-2 rounded-full text-[10px] font-bold">
                            BOLETERÍA AÉREA
                          </span>
                        )}
                      </h6>
                      {message.viajes.map((viaje, idx) => (
                        <div key={viaje.id || idx} className="mb-3 bg-slate-50 p-3 rounded">
                          <div className="text-blueGray-700 font-bold text-xs uppercase mb-2">
                            Boleto #{viaje.numero_linea || idx + 1}
                          </div>
                          <div className="flex flex-wrap">
                            {VIAJE_CAMPOS.map((campo) => (
                              <div key={campo.key} className="w-full lg:w-1/3 text-start mb-1 pr-3">
                                <label className="text-blueGray-700 font-bold me-2">{campo.label}:</label>
                                <span className="text-blueGray-700">
                                  {formatText(viaje[campo.key] || '') || '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {(message.tipo_documento === 'ND' || message.tipo_documento === 'NC') && message.factura_afectada_rel && (
                  <hr className="my-4 border-b border-blueGray-300"/>
                )}
                {/* Factura afectada */}
                {(message.tipo_documento === 'ND' || message.tipo_documento === 'NC') && message.factura_afectada_rel && (
                  <div className="mb-4 px-2 mt-0">
                    <h6 className="text-lg font-bold text-blueGray-700 mb-2">Factura afectada</h6>
                    <div className="flex flex-wrap justify-between">
                      <div className="w-1/5 text-start">
                        <label className="font-bold text-blueGray-700">Número de control:</label>
                        <div>{formatText(message.factura_afectada_rel.numero_control)}</div>
                      </div>
                      <div className="w-1/5 text-center">
                        <label className="font-bold text-blueGray-700">Fecha de emisión:</label>
                        <div>{formatDateTime(message.factura_afectada_rel.fecha_emision)}</div>
                      </div>
                      <div className="w-1/5 text-end">
                        <label className="font-bold text-blueGray-700">Total:</label>
                        <div>{formatMoney(message.factura_afectada_rel.total_neto)}</div>
                      </div>
                    </div>
                    <hr className="my-4 border-b border-blueGray-300"/>
                  </div>
                )}

                {/* Tabla de items */}
                <hr className="my-6 border-b border-blueGray-300"/>
                {/* (2026-08-02, ítem 32-ii) Columnas "Desc." (descuento por
                    MONTO fijo) y "Recargo" — los mismos datos y el mismo orden
                    que la modal de factura. */}
                <div className="px-5 flex justify-between border-4 border-double p-2 mb-2">
                  <span className="lg:w-4/12 text-center font-bold">Producto</span>
                  <span className="lg:w-2/12 text-center font-bold">Cantidad</span>
                  <span className="lg:w-2/12 text-center font-bold">Impuesto</span>
                  <span className="lg:w-3/12 text-center font-bold">Precio Unitario</span>
                  <span className="lg:w-2/12 text-center font-bold">Desc %</span>
                  <span className="lg:w-2/12 text-center font-bold">Desc.</span>
                  <span className="lg:w-2/12 text-center font-bold">Recargo</span>
                  <span className="lg:w-3/12 text-center font-bold">Total</span>
                </div>

                {message.items && message.items.length > 0 ? (
                  message.items.map(item => {
                    // (ítem 32-ii/iii) Resolución `_aplicada` + total de línea:
                    // helpers compartidos con InvoiceDetailView. El total ya NO
                    // ignora el descuento por monto fijo ni el recargo de línea.
                    const descuentoMonto = valorAplicado(item, 'descuento_monto');
                    const recargoMonto = valorAplicado(item, 'recargo_monto');
                    return (
                    <div key={item.id} className="px-5 flex justify-between border-b py-1">
                      <span className="lg:w-4/12 text-start">{item.producto ? `${item.producto.sku}-${item.producto.nombre}` : item.tipo_documento !== 'ND' ? `${item.descripcion}`: 'N/A'}</span>
                      <span className="lg:w-2/12 text-center">{formatDecimal(item.cantidad)}</span>
                      <span className="lg:w-2/12 text-center">{item.iva_categoria_id ? formatDecimal(item.iva_categoria.tasa_porcentaje) : '0,00'}</span>
                      {/* (H15, auditoría 2026-08-02) El precio unitario se
                          resuelve con el MISMO helper que usa el Total
                          (`precioUnitarioAplicado`): antes la celda mostraba la
                          columna plana mientras el total ya usaba la resuelta,
                          de modo que en un documento multimoneda cantidad ×
                          precio no cuadraba con el total de su propia fila. */}
                      <span className="lg:w-3/12 text-right">{formatDecimal(precioUnitarioAplicado(item, message.tipo_documento))}</span>
                      <span className="lg:w-2/12 text-center">{formatDecimal(item.descuento_porcentaje) || '0,00'}</span>
                      <span className="lg:w-2/12 text-right">{descuentoMonto > 0 ? formatDecimal(descuentoMonto) : '—'}</span>
                      <span className="lg:w-2/12 text-right">{recargoMonto > 0 ? formatDecimal(recargoMonto) : '—'}</span>
                      <span className="lg:w-3/12 text-right">{formatDecimal(totalLineaMostrado(item, message.tipo_documento))}</span>
                    </div>
                    );
                  })
                ) : (
                  <div className="px-5 text-center border-b py-2">
                    No hay líneas asociadas
                  </div>
                )}
                <div className="flex flex-wrap">
                  <div className="flex w-full lg:w-8/12 px-2 ml-0 lg:ml-0 mt-3 pb-3 flex-1 min-w-0 border border-gray-200 rounded-lg shadow-sm">
                    <div className="w-full lg:w-9/12 px-1 mt-3 pl-0">
                      <div className="relative w-full mb-3 text-end">
                        <div className="px-5 flex justify-between border-4 border-double p-2 mb-2">
                          <span className="lg:w-4/12 text-center font-bold">Medio de pago</span>
                          <span className="lg:w-4/12 text-center font-bold">Banco</span>
                          <span className="lg:w-4/12 text-center font-bold">Monto</span>
                        </div>

                        {message.pagos && message.pagos.length > 0 ? (
                          message.pagos.map(pago => (
                            <div key={pago.id} className="px-5 flex justify-between border-b py-1">
                              <span className="lg:w-4/12 text-start">{pago.metodo_pago_nombre ? formatText(pago.metodo_pago_nombre) : ''}</span>
                              <span className="lg:w-4/12 text-center">{pago.banco && pago.aplica_igtf == false ? formatText(pago.banco) : 'N/A'}</span>
                              <span className="lg:w-4/12 text-right">{formatMoney(pago.monto, pago.moneda)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="px-5 text-center border-b py-2">
                            No se encontraron pagos asociadas
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full lg:w-3/12 px-1 pr-0 pt-1 pb-1 mt-0">
                      {(message.monto_pagado_divisas > 0.0) ?
                        (<div className="mt-3 border border-gray-200 rounded-lg shadow-sm bg-gray-50">

                          <div className="p-4 pb-2 border-b border-gray-200">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">
                              Total pagado en divisas
                            </label>
                          </div>

                          <div className="p-4 pt-0 text-right border-b border-gray-200">
                            <label className="border-0 px-3 text-blueGray-600 rounded text-lg font-bold">
                              {formatMoney(message.monto_pagado_divisas, message.moneda)}
                            </label>
                          </div>

                          <div className="p-4 pb-2 border-b border-gray-200">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">
                              {'IGTF ' + formatDecimal(message.igtf_porcentaje) + '%' }
                            </label>
                          </div>

                          <div className="p-4 pt-0 text-right">
                            <label className="border-0 px-3 text-blueGray-600 rounded text-lg font-bold">
                              {formatMoney(message.igtf_monto_aplicada != null ? message.igtf_monto_aplicada : (message.igtf_monto || 0), message.moneda)}
                            </label>
                          </div>
                        </div>)
                        :
                        (<div className="apply_igtf mt-3 rounded-lg shadow-sm bg-gray-50">
                        </div>)
                      }
                    </div>
                  </div>
                  <div className="w-full lg:w-4/12 px-0 mt-3">
                    {/* (H7, auditoría 2026-08-02) Esta modal suma las FILAS
                        HIJAS `recargos_globales`; la vista pública imprime el
                        agregado de cabecera `totales.total_recargos`. Ver la
                        nota extendida en InvoiceDetailView.js. Sin cambio de
                        cifras: es una diferencia de origen del dato, no de
                        cálculo. */}
                    {/* (2026-08-02, ítem 32-i) RECARGOS GLOBALES de la proforma.
                        `recargos_globales` [{descripcion, monto}] ya viajaba en
                        el schema de detalle (schemas.py:1392) y no se pintaba.
                        En PREFACTURA `monto` está en la moneda de la OPERACIÓN
                        (la misma en que se muestran los totales de esta modal),
                        así que se usa la columna plana — a diferencia de la
                        factura, donde `monto` es C1/VES y hay que leer
                        `monto_aplicada` (models.py:1876-1883 vs :1906-1910).
                        (2026-08-03, P3) La sección de DESCUENTOS globales ya SÍ
                        se construye (tabla hija `descuentos_prefactura`,
                        expuesta como `descuentos_globales`); va justo debajo con
                        el mismo criterio de moneda. */}
                    {(message.recargos_globales || []).length > 0 && (
                      <div className="w-full mb-3 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg mb-1">Recargos</label>
                        {message.recargos_globales.map((rg, idx) => (
                          <div key={rg.id || idx} className="flex w-full text-sm justify-between">
                            <span className="text-blueGray-600 pr-2 truncate">
                              {formatText(rg.descripcion || '')}
                            </span>
                            <span className="text-blueGray-700 whitespace-nowrap">
                              {formatMoney(Number(rg.monto) || 0, message.moneda)}
                            </span>
                          </div>
                        ))}
                        <div className="flex w-full justify-end border-t border-gray-200 mt-1 pt-1">
                          <label className="text-blueGray-600 font-bold text-md">
                            Total recargos:{' '}
                            {formatMoney(
                              Math.round(
                                message.recargos_globales.reduce(
                                  (acc, rg) => acc + (Number(rg.monto) || 0), 0,
                                ) * 100,
                              ) / 100,
                              message.moneda,
                            )}
                          </label>
                        </div>
                      </div>
                    )}
                    {/* (2026-08-03, P3) DESCUENTOS GLOBALES de la proforma.
                        `descuentos_globales` [{descripcion, monto}] viaja en el
                        schema de detalle. En PREFACTURA `monto` está en la moneda
                        de la OPERACIÓN (la misma en que se muestran los totales
                        de esta modal), así que se usa la columna plana — igual
                        que en el bloque de recargos de arriba. Condicional: sin
                        descuentos globales la modal se ve exactamente igual. */}
                    {(message.descuentos_globales || []).length > 0 && (
                      <div className="w-full mb-3 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg mb-1">Descuentos</label>
                        {message.descuentos_globales.map((dg, idx) => (
                          <div key={dg.id || idx} className="flex w-full text-sm justify-between">
                            <span className="text-blueGray-600 pr-2 truncate">
                              {formatText(dg.descripcion || '')}
                            </span>
                            <span className="text-blueGray-700 whitespace-nowrap">
                              {formatMoney(Number(dg.monto) || 0, message.moneda)}
                            </span>
                          </div>
                        ))}
                        <div className="flex w-full justify-end border-t border-gray-200 mt-1 pt-1">
                          <label className="text-blueGray-600 font-bold text-md">
                            Total descuentos:{' '}
                            {formatMoney(
                              Math.round(
                                message.descuentos_globales.reduce(
                                  (acc, dg) => acc + (Number(dg.monto) || 0), 0,
                                ) * 100,
                              ) / 100,
                              message.moneda,
                            )}
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="flex w-full mb-1">
                      <div className="w-full lg:w-5/12 px-4 mt-3">
                        <label className="block text-blueGray-600 font-bold text-lg">Base imponible</label>
                      </div>
                      <div className="w-full lg:w-7/12 px-4 mt-3 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(message.total_base, message.moneda)}
                        </label>
                      </div>
                    </div>
                    <div className="flex w-full mb-1">
                      <div className="w-full lg:w-4/12 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg">I.V.A.</label>
                      </div>
                      <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(message.total_impuestos, message.moneda)}
                        </label>
                      </div>
                    </div>
                    <div className="flex w-full mb-1">
                      <div className="w-full lg:w-4/12 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg">IGTF</label>
                      </div>
                      <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(message.igtf_monto || 0, message.moneda)}
                        </label>
                      </div>
                    </div>
                    <div className="flex w-full mb-3">
                      <div className="w-full lg:w-4/12 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg">Total</label>
                      </div>
                      <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(Number(message.total || 0) - Number(message.igtf_monto || 0), message.moneda)}
                        </label>
                      </div>
                    </div>
                    {(message.monto_pagado_divisas > 0.0) ?
                    (<div className="flex w-full mb-3">
                        <div className="w-full lg:w-4/12 px-4">
                          <label className="block text-blueGray-600 font-bold text-lg">Total + IGTF</label>
                        </div>
                        <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                          <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1 ">
                          {formatMoney(message.total, message.moneda)}
                          </label>
                        </div>
                      </div>)
                      :
                      (<div className="hidden flex w-full mb-3">
                        <div className="w-full lg:w-3/12 px-4">
                        </div>
                        <div className="w-full lg:w-9/12 px-4 text-right">
                        </div>
                      </div>)
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER (en flujo flex, no absoluto: evita solaparse con el body) */}
        <div className="shrink-0 w-full flex justify-center py-4 border-t border-slate-200 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-semibold rounded-xl transition"
          >
            Cerrar
          </button>
        </div>

      </div>
    </div>
  );
}

export default ModalProformas;